import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";

import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { useAlert } from "@/hooks/useAlert";
import { markSessionInitialized } from "@/hooks/useClearLocalStorageOnFirstTab";
import { useQueryParam } from "@/hooks/useQueryParam";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { getCustomer } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";
import { consumeMagicLinkSession } from "@/utils/consumeMagicLinkSession";
import { getMagicLinkConsumeErrorKey } from "@/utils/magicLinkErrors";

/**
 * Consumes a magic-link hash from the email and signs the customer in.
 */
export const MagicLogin = () => {
  const [hash] = useQueryParam("hash");
  const navigate = useNavigate();
  const showAlert = useAlert();
  const { t } = useTranslation();
  const hasStartedConsumeRef = useRef(false);

  useEffect(() => {
    if (!hash || hasStartedConsumeRef.current) {
      return;
    }

    hasStartedConsumeRef.current = true;

    consumeMagicLinkSession(hash).then(async ({ success, error }) => {
      if (success) {
        // Magic link bypasses LoginRedirect, which normally pre-sets the session
        // marker so App's clearLocalStorageOnce skips its destructive
        // localStorage.clear(). Without this, Jotai's onMount re-reads null
        // from localStorage after the clear and logs the user back out.
        markSessionInitialized();

        try {
          const customer = await getCustomer();

          navigate(
            customer.initialPasswordChanged === false
              ? portalRoutes.initialPasswordSetup
              : portalRoutes.cockpit.base,
            { replace: true },
          );
        } catch {
          navigate(portalRoutes.cockpit.base, { replace: true });
        }

        return;
      }

      if (error) {
        showAlert({
          type: AlertTypes.error,
          text: t(getMagicLinkConsumeErrorKey(error)),
        });
      }

      navigate(portalRoutes.welcome, { replace: true });
    });
  }, [hash, navigate, showAlert, t]);

  if (!hash) {
    return <Navigate to="/404" replace />;
  }

  return <BrandLoadingScreen />;
};
